import type { GridPoint, WorldPoint } from './types';

/**
 * 2:1 isometric projection, shared by rendering, picking and (later) movement.
 *
 * Convention:
 *  - Grid (x, y) is the logical tile index. Grid X increases towards the
 *    screen's lower-right, grid Y towards the lower-left.
 *  - `gridToWorld` returns the world-space CENTRE of the tile's diamond.
 *  - Tile (0, 0) is the top corner of the map; its centre is world (0, 0).
 *
 * The functions are pure and engine-independent so they are unit tested
 * without Phaser.
 */
export class IsoProjection {
  readonly tileWidth: number;
  readonly tileHeight: number;
  readonly halfWidth: number;
  readonly halfHeight: number;

  constructor(tileWidth: number, tileHeight: number) {
    if (tileWidth <= 0 || tileHeight <= 0) {
      throw new Error(`Invalid tile size ${tileWidth}x${tileHeight}`);
    }
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;
    this.halfWidth = tileWidth / 2;
    this.halfHeight = tileHeight / 2;
  }

  /** World-space centre of a (possibly fractional) grid position. */
  gridToWorld(gx: number, gy: number): WorldPoint {
    return {
      x: (gx - gy) * this.halfWidth,
      y: (gx + gy) * this.halfHeight,
    };
  }

  /** Continuous (fractional) grid position for a world point; inverse of gridToWorld. */
  worldToGridFloat(wx: number, wy: number): { x: number; y: number } {
    const a = wy / this.tileHeight;
    const b = wx / this.tileWidth;
    return { x: a + b, y: a - b };
  }

  /**
   * The tile whose diamond contains the world point. Points exactly on an
   * edge resolve consistently (round-half-up on both axes).
   */
  worldToGrid(wx: number, wy: number): GridPoint {
    const f = this.worldToGridFloat(wx, wy);
    return { x: Math.floor(f.x + 0.5), y: Math.floor(f.y + 0.5) };
  }

  /** The four diamond corners of a tile in world space: top, right, bottom, left. */
  tileCorners(gx: number, gy: number): [WorldPoint, WorldPoint, WorldPoint, WorldPoint] {
    const c = this.gridToWorld(gx, gy);
    return [
      { x: c.x, y: c.y - this.halfHeight },
      { x: c.x + this.halfWidth, y: c.y },
      { x: c.x, y: c.y + this.halfHeight },
      { x: c.x - this.halfWidth, y: c.y },
    ];
  }

  /** Axis-aligned world bounds of a width x height map (diamond's bounding box). */
  mapWorldBounds(width: number, height: number): { x: number; y: number; width: number; height: number } {
    const left = this.gridToWorld(0, height - 1).x - this.halfWidth;
    const right = this.gridToWorld(width - 1, 0).x + this.halfWidth;
    const top = this.gridToWorld(0, 0).y - this.halfHeight;
    const bottom = this.gridToWorld(width - 1, height - 1).y + this.halfHeight;
    return { x: left, y: top, width: right - left, height: bottom - top };
  }
}
